/**
 * Event Idempotency Tracking
 *
 * Tracks processed event IDs to skip duplicate events from re-emissions,
 * reconnections, or race conditions between socket handlers.
 *
 * Part of Phase 1: Event Deduplication & Connection Gating
 */

// Maximum number of event IDs to track (bounded for memory)
const MAX_PROCESSED_IDS = 1000;

// How many to prune when limit is reached
const PRUNE_COUNT = 200;

// TTL for event IDs (5 minutes) - events older than this are considered stale
const EVENT_TTL_MS = 5 * 60 * 1000;

interface EventRecord {
  timestamp: number;
  eventType: string;
}

// Map of event ID -> record with timestamp and type
const processedEvents = new Map<string, EventRecord>();

// Telemetry counters
let duplicatesBlocked = 0;
let eventsProcessed = 0;

/**
 * Mark an event as processed and check if it's a duplicate.
 *
 * @param eventId - Unique identifier for the event (e.g., message_id)
 * @param eventType - Type of event for logging (e.g., 'message:new')
 * @returns true if this is a NEW event (should process), false if DUPLICATE (skip)
 *
 * @example
 * ```ts
 * socket.on('message:new', (data) => {
 *   if (!markEventProcessed(data.messageId, 'message:new')) {
 *     console.log('Skipping duplicate message:new event');
 *     return;
 *   }
 *   // Process the event...
 * });
 * ```
 */
export function markEventProcessed(eventId: string, eventType: string): boolean {
  // Check if already processed
  const existing = processedEvents.get(eventId);
  if (existing) {
    // Check if within TTL window
    const age = Date.now() - existing.timestamp;
    if (age < EVENT_TTL_MS) {
      duplicatesBlocked++;
      console.log(
        `[EventIdempotency] Duplicate blocked: ${eventType} (${eventId}) - age: ${age}ms`
      );
      return false;
    }
    // Event is stale, allow re-processing (update timestamp)
    console.log(
      `[EventIdempotency] Stale event re-processed: ${eventType} (${eventId}) - age: ${age}ms`
    );
  }

  // Prune if at capacity
  if (processedEvents.size >= MAX_PROCESSED_IDS) {
    pruneOldest();
  }

  // Mark as processed
  processedEvents.set(eventId, {
    timestamp: Date.now(),
    eventType,
  });
  eventsProcessed++;

  return true;
}

/**
 * Check if an event has been processed without marking it.
 *
 * @param eventId - Event ID to check
 * @returns true if event was previously processed (within TTL)
 */
export function isEventProcessed(eventId: string): boolean {
  const existing = processedEvents.get(eventId);
  if (!existing) return false;

  const age = Date.now() - existing.timestamp;
  return age < EVENT_TTL_MS;
}

/**
 * Generate a composite event ID for events that don't have unique IDs.
 * Uses event type + session ID + content hash for deduplication.
 *
 * @param eventType - Type of event
 * @param sessionId - Session ID
 * @param content - Event content (will be hashed)
 * @returns Composite event ID
 */
export function generateEventId(
  eventType: string,
  sessionId: string,
  content?: string
): string {
  const contentHash = content
    ? simpleHash(content.slice(0, 100)) // Only hash first 100 chars for performance
    : 'no-content';
  return `${eventType}:${sessionId}:${contentHash}`;
}

/**
 * Simple string hash for content-based deduplication.
 * Not cryptographic - just for bucketing.
 */
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Prune oldest entries from the processed events map.
 * Uses FIFO based on insertion order (Map maintains insertion order).
 */
function pruneOldest(): void {
  const keys = Array.from(processedEvents.keys());
  const toDelete = keys.slice(0, PRUNE_COUNT);

  console.log(
    `[EventIdempotency] Pruning ${toDelete.length} oldest entries (size: ${processedEvents.size})`
  );

  toDelete.forEach((key) => processedEvents.delete(key));
}

/**
 * Clear all processed event records.
 * Use on logout or major state reset.
 */
export function clearProcessedEvents(): void {
  console.log(`[EventIdempotency] Clearing all ${processedEvents.size} entries`);
  processedEvents.clear();
  duplicatesBlocked = 0;
  eventsProcessed = 0;
}

/**
 * Get telemetry stats for monitoring.
 */
export function getIdempotencyStats(): {
  totalProcessed: number;
  duplicatesBlocked: number;
  currentSize: number;
  blockRate: number;
} {
  const total = eventsProcessed + duplicatesBlocked;
  return {
    totalProcessed: eventsProcessed,
    duplicatesBlocked,
    currentSize: processedEvents.size,
    blockRate: total > 0 ? duplicatesBlocked / total : 0,
  };
}

/**
 * Session join tracking - prevents joining same session multiple times.
 */
const joinedSessions = new Set<string>();

/**
 * Track a session join and check if already joined.
 *
 * @param sessionId - Session ID being joined
 * @returns true if this is a NEW join (should proceed), false if ALREADY JOINED (skip)
 */
export function markSessionJoined(sessionId: string): boolean {
  if (joinedSessions.has(sessionId)) {
    console.log(`[EventIdempotency] Session already joined: ${sessionId}`);
    return false;
  }

  joinedSessions.add(sessionId);
  console.log(`[EventIdempotency] Session joined: ${sessionId}`);
  return true;
}

/**
 * Mark a session as left/disconnected.
 */
export function markSessionLeft(sessionId: string): void {
  joinedSessions.delete(sessionId);
  console.log(`[EventIdempotency] Session left: ${sessionId}`);
}

/**
 * Check if a session is currently joined.
 */
export function isSessionJoined(sessionId: string): boolean {
  return joinedSessions.has(sessionId);
}

/**
 * Clear all joined session tracking.
 * Call on disconnect or logout.
 */
export function clearJoinedSessions(): void {
  console.log(`[EventIdempotency] Clearing ${joinedSessions.size} joined sessions`);
  joinedSessions.clear();
}

/**
 * Get list of currently joined sessions.
 */
export function getJoinedSessions(): string[] {
  return Array.from(joinedSessions);
}
